import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import Layout from "../../components/Layout";
import { useAppContext } from "../../context/AppContext";
import ApiService from "../../services/api";

const SelfieCapturePage: React.FC = () => {
  const initializing = useRef<boolean>(false);
  const hasInitialized = useRef<boolean>(false);
  const [smartCaptureInitialized, setSmartCaptureInitialized] = useState(false);
  const {
    setSelfieImage,
    setCurrentView,
    setProfileExists,
    setProfileId,
    sessionId,
    setSkinScores,
    setSnackbar,
    updateRecommendations,
    getTranslation,
    token,
    setEnableSessionToCreateNew,
  } = useAppContext();
  const smartCaptureContainer = useRef<HTMLDivElement>(null);

  const setSmartCaptureConfig = useCallback(() => {
    const lib = window.OrboSmartCapture;
    lib.withConfig({
      STATIC_ASSETS_BASE_URL: "https://makeup.sdk.orbo.ai/",
      ASSETS_BASE_URL: "/smart-capture/",
      UIConfig: {
        SMART_CAPTURE: {
          GUIDELINES: {
            GUIDELINE1: getTranslation("Txt664"),
            GUIDELINE2: getTranslation("Txt663"),
          },
          MSG1: getTranslation("Txt808"),
          MSG2: getTranslation("Txt809"),
          MSG3: getTranslation("Txt810"),
          MSG4: getTranslation("Txt811"),
          MSG5: getTranslation("Txt656"),
          MSG6: getTranslation("Txt659"),
          MSG7: getTranslation("Txt662"),
          MSG8: getTranslation("Txt812"),
          MSG9: getTranslation("Txt666"),
          MSG10: getTranslation("Txt665"),
          MSG11: getTranslation("Txt655"),
          MSG12: getTranslation("Txt658"),
          MSG13: getTranslation("Txt661"),
        },
      },
    });
  }, [getTranslation]);


  const onCapture = useCallback(async (picBlob: Blob) => {
    const lib = window.OrboSmartCapture;
    try {
      initializing.current = true;
      setSelfieImage(picBlob);

      const api = new ApiService(sessionId, token);
      await api.uploadImage(picBlob);
      const scores = await api.getSkinAnalysis();
      // await api.getDistributorReport();

      setProfileExists(scores.profile_exists);
      setProfileId(scores.profile_id);
      setSkinScores({
        skinScores: scores.output_score,
        annotations: scores.annotations,
      });
      const recommendations = await api.getProductRecommendations();

      updateRecommendations(recommendations);

      setCurrentView("scores");
      initializing.current = false;
    } catch (error: any) {
      initializing.current = false;
      lib.setMode("PREVIEW");
      console.error("SelfieCapturePage Error Details:", {
        error: error,
        message: error instanceof Error ? error.message : "Unknown error",
        stack: error instanceof Error ? error.stack : "No stack trace",
        name: error instanceof Error ? error.name : "Unknown error type",
      });

      let snackbarMessageCode = "Err86";

      setSnackbar({
        message: getTranslation(snackbarMessageCode),
        open: true,
        severity: "error",
      });
    }
  }, [setSelfieImage, sessionId, token, setProfileExists, setProfileId, setSkinScores, updateRecommendations, setCurrentView, getTranslation, setSnackbar]);

  const onSmartCaptureInitialized = useCallback(() => {
    setSmartCaptureInitialized(true);
  }, []);

  // Store latest callback refs to avoid stale closures
  const onCaptureRef = useRef(onCapture);
  const onSmartCaptureInitializedRef = useRef(onSmartCaptureInitialized);
  const setSmartCaptureConfigRef = useRef(setSmartCaptureConfig);
  
  // Update refs when callbacks change
  useEffect(() => {
    onCaptureRef.current = onCapture;
    onSmartCaptureInitializedRef.current = onSmartCaptureInitialized;
    setSmartCaptureConfigRef.current = setSmartCaptureConfig;
  }, [onCapture, onSmartCaptureInitialized, setSmartCaptureConfig]);

  useEffect(() => {
    // Prevent re-initialization if already initialized
    if (hasInitialized.current) {
      return;
    }

    // Reset state when component mounts
    setSmartCaptureInitialized(false);
    initializing.current = false;
    hasInitialized.current = true;

    const lib = window.OrboSmartCapture;
    
    // Stop any existing camera instance first
    if (lib && typeof lib.stop === "function") {
      try {
        lib.stop();
      } catch (error) {
        // Ignore errors when stopping
      }
    }

    // Clear the container
    if (smartCaptureContainer.current) {
      smartCaptureContainer.current.innerHTML = "";
    }

    // Small delay to ensure cleanup completes
    const timeoutId = setTimeout(() => {
      setEnableSessionToCreateNew(true);
      setSmartCaptureConfigRef.current();
      
      lib.on("PIC_CAPTURED", onCaptureRef.current);
      lib.on("SMART_CAPTURE_STARTED", onSmartCaptureInitializedRef.current);

      const fn = async () => {
        try {
          if (
            smartCaptureContainer &&
            smartCaptureContainer.current &&
            !initializing.current
          ) {
            initializing.current = true;
            await lib.captureSelfie(
              smartCaptureContainer.current
            );
          }
        } catch (error: any) {
          initializing.current = false;
          setSmartCaptureInitialized(true); 
          console.error("SelfieCapturePage Error Details:", {
            error: error,
            message: error instanceof Error ? error.message : "Unknown error",
            stack: error instanceof Error ? error.stack : "No stack trace",
            name: error instanceof Error ? error.name : "Unknown error type",
          });

          let snackbarMessageCode = "Err86";
          if (error?.code === 5) {
            snackbarMessageCode = "Err93";
          }

          setSnackbar({
            message: getTranslation(snackbarMessageCode),
            open: true,
            severity: "error",
          });
        }
      };

      fn();
    }, 300);

    // Cleanup function
    return () => {
      clearTimeout(timeoutId);
      hasInitialized.current = false;
      if (lib) {
        if (typeof lib.off === "function") {
          lib.off("PIC_CAPTURED", onCaptureRef.current);
          lib.off("SMART_CAPTURE_STARTED", onSmartCaptureInitializedRef.current);
        }
        if (typeof lib.stop === "function") {
          try {
            lib.stop();
          } catch (error) {
            // Ignore errors during cleanup
          }
        }
      }
      initializing.current = false;
      if (smartCaptureContainer.current) {
        smartCaptureContainer.current.innerHTML = "";
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run once on mount

  return (
    <Layout showBackButton={false}>
      {!smartCaptureInitialized && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "75vh",
          }}
        >
          <CircularProgress size={60} sx={{ color: "var(--color-text-primary)" }} />
        </Box>
      )}
      <Box
        ref={smartCaptureContainer}
        sx={{
          maxHeight: "75vh",
          height: "75vh",
          display: smartCaptureInitialized ? "block" : "none",
        }}
      ></Box>
    </Layout>
  );
};

export default SelfieCapturePage;
